import { InternalId } from '@edgebox/data-definition-kit';
import {
  MonitoringPromptKpi,
  MonitoringPromptRunEntityMatchType,
  MonitoringPromptRunParsed,
  MonitoringPromptRunStatus,
  MonitoringPromptType,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyFacet,
} from '@edgebox/sync-core-data-definitions';
import { ClientMonitoringPromptRunEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { plainToInstance } from 'class-transformer';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { SyncCoreApiContext } from '../../../../contexts/SyncCoreApiContext';
import { searchOfBrandPresenceScreenAddress, useBrandPresenceScreenAddress } from '../brandPresenceScreenQuery';
import {
  SuggestedTermIcon,
  TaxonomyTermName,
  TaxonomyTermNameBadge,
  useTaxonomyTermName,
  useTermColors,
  useTermStatuses,
  useTermRowVisibility,
} from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import {
  EntityOwnership,
  PromptDisplay,
  PromptLanguageMode,
  VISIBLE_MENTION_FACETS,
  buildRunListFilters,
  classifyEntityOwnership,
  targetPositionInAnswer,
  promptLabelOf,
  UNKNOWN_PROMPT_LABEL,
  isEnglishLocaleName,
  translationTextFor,
  parseAiAnswersFilterState,
  ORIGINAL_LANGUAGE_HINT,
  isPendingRunStatus,
  pickPromptText,
  shouldMarkOriginalLanguage,
  mentionTermKey,
  AiAnswerEntityLike,
  resolveWinner,
  toneForRow,
  shareOfVoicePercent,
} from '../GovernanceAnalyticsAiAnswers.utils';
import {
  AMBIGUOUS_RECOMMENDATION_LABEL,
  AMBIGUOUS_RECOMMENDATION_TOOLTIP,
  isAmbiguousRecommendation,
  buildCellPaint,
  buildLinePaint,
  ATTRIBUTE_SHARE_COLUMN_LABEL,
  ATTRIBUTE_VERDICT_HINT,
  AttributeScoreLike,
  attributeBarWidthPercent,
  attributeShareCeiling,
  buildAttributeGroups,
  CitationLike,
  describeAttributeBar,
  lineIndicesForUnits,
  shareToPercent,
  PaintLabel,
  PaintResolution,
  PaintUnit,
  referenceKey,
  SpotlightAspect,
  unitIdsForAspectTerm,
  SPOTLIGHT_ROW_CLASS_NAME,
  SPOTLIGHT_TABLE_ROW_CLASS_NAME,
  spotlightRowStateClassName,
  SpotlightSubject,
  SpotlightTargets,
  isSpotlightSubjectSelected,
  toggleSpotlightSubject,
  unionSpotlightTargets,
  spotlightTargets,
} from './GovernanceAiAnswerDetail.utils';
import { AnswerTargetPosition } from './AnswerTargetPosition';
import { MarkdownContentBox } from './MarkdownContentBox';
import { BrandMatcher, HighlightedAnswer, PassageInteraction } from './HighlightedAnswer';
import { AnswerPassageCard, PassageCardMeasures } from './AnswerPassageCard';
import { answerPassageElements } from './answerPassageElements';
import {
  attributeVerdictFor,
  buildAnswerPassageIndex,
  buildAttributeVerdicts,
  buildVerdictLens,
  PASSAGE_VERDICT_LABELS,
  passageExcerpt,
  VERDICT_GRADES_BEST_FIRST,
  VERDICT_LENS_COLORS,
} from './answerPassages.utils';
import { GovernanceSegmentedSwitch, GovernanceSwitchOption } from './GovernanceSegmentedSwitch';
import { findPassageElement, PassageCardLayer } from './PassageCardLayer';
import { MonitoringPromptRunStatusBadge } from './MonitoringPromptRunDetail';
import { SentimentThumb } from './SentimentThumb';
import { EntityShareAndSentiment } from './EntityShareAndSentiment';
import { UntrackedRivalsChip } from './UntrackedRivalsChip';
import { mutedColorFor, termStatusPresentation } from './termStatusPresentation';
import { GOVERNANCE_COMPARISON_PALETTE, readPrimaryColor, VERDICT_PALETTE } from './governanceThemeColors';
import { EntityFavicon, FaviconLoadGate } from './SourceFavicon';
import { AnswerCitedSources } from './AnswerCitedSources';
import { SourceGrouping } from './citationSources.utils';
import { useAuthoritySources } from './useAuthoritySources';
import { useEntityDomains } from './useEntityDomains';
import { formatRecommendationEvidence } from './recommendationEvidence';
import {
  applyTermStatusToColorMap,
  buildEntityColorMap,
  buildEntityLegend,
  collectColorableTermKeys,
  colorForEntityName,
  ColorableMention,
  LegendEntry,
  readableEntityTextColor,
} from './entityPalette';
import { getCachedTaxonomyTermsByFacet } from '../Onboarding/ContentInventory/Components/useCachedTaxonomyData';
import { PurposeTermLike } from './purposeTabs.utils';
import { isKpiShown, purposeKpiMask } from './purposeKpiMask.utils';
import { PASSAGE_TONE_LABEL, TONE_KPI } from '../toneMetric';
import { PurposeKpiScopeNote } from './PurposeKpiScopeNote';
import { BrandPresenceEyebrow } from './BrandPresencePageHeader';
import { SkeletonBlock, SkeletonRegion } from './BrandPresenceSkeleton';
import {
  BP_ANSWER_PROSE_CLASSES,
  BP_ANSWER_SURFACE_CLASSES,
  BP_CARD_CLASSES,
  BP_TOOLBAR_BUTTON_SMALL_CLASSES,
} from './brandPresenceClasses';

/** The line lengths of the answer drawn while it loads, varied so the block reads as prose. */
const ANSWER_SKELETON_LINE_WIDTHS = [
  'h-3.5 w-11/12',
  'h-3.5 w-full',
  'h-3.5 w-5/6',
  'h-3.5 w-full',
  'h-3.5 w-3/4',
  'h-3.5 w-full',
  'h-3.5 w-10/12',
  'h-3.5 w-3/5',
];

/** The one toolbar above the answer: its heading, the key to its colours and the controls that change them. */
const ANSWER_TOOLBAR_CLASSES =
  'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-white/80 px-3.5 py-2 shadow-card ring-1 ring-slate-200/70';

/**
 * While a passage's card lies over the sidebar, the sidebar steps back — faded, and slightly
 * blurred where motion is welcome — and comes back when the card closes. The card's layer marks
 * itself with `data-covers-sidebar` while it is shown beside the answer.
 */
const SIDEBAR_UNDER_CARD_CLASSES =
  'transition-[opacity,filter] duration-200 ease-out group-has-[[data-covers-sidebar]]/answer:opacity-40 motion-safe:group-has-[[data-covers-sidebar]]/answer:blur-[1.5px]';

/** The recommendation card: the grade of recommending you, or of arguing against you, from the verdict palette. */
const RECOMMENDATION_CARD_OWN_CLASSES = VERDICT_PALETTE.excellent.surfaceClassName;
const RECOMMENDATION_CARD_RIVAL_CLASSES = VERDICT_PALETTE.veryNegative.surfaceClassName;

/** How the answer's passages are coloured: by the brand each is about, or by the verdict it gives one brand. */
type PassageColouring = 'brand' | 'verdict';

const PASSAGE_COLOURING_OPTIONS: GovernanceSwitchOption<PassageColouring>[] = [
  { value: 'brand', label: 'Brand' },
  { value: 'verdict', label: PASSAGE_TONE_LABEL },
];

/**
 * How long a passage's card waits before closing once the pointer leaves it. Long enough to cross the
 * gap to the next passage without the card blinking out and back, short enough to feel immediate.
 */
const PASSAGE_HOVER_RELEASE_MS = 120;

/** Explains what the prompt-language toggle does, in the reader's terms. */
const PROMPT_LANGUAGE_HINT =
  'Prompts are shown in English when the template has an English version. Tick this to read the prompt in the language it was asked in.';

/** Distinct palette for competitor tinting (own brand uses the theme's primary colour). */
const COMPETITOR_PALETTE = GOVERNANCE_COMPARISON_PALETTE;

function translateCountry(country?: string): string {
  if (!country) {
    return '';
  }
  try {
    return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptCountry', country as never);
  } catch {
    return String(country);
  }
}

/**
 * One row of "Who's mentioned": the brand, its share of voice, and its sentiment.
 *
 * The whole row carries the term's status — muting only the name would leave confident-looking
 * numbers beside a hedged label. Share and sentiment render through the same component the answers
 * list uses, so the two screens cannot drift apart again.
 */
function MentionRow({
  api,
  entity,
  color,
  nameColor,
  domain,
  untrackedRivals,
  showShareOfVoice,
  showSentiment,
  onSpotlight,
  onClearSpotlight,
  onToggleSelected,
  selected,
}: {
  api?: ISyncCoreApi;
  entity: AiAnswerEntityLike;
  color?: string;
  /** The colour for the name text: a stored colour darkened for readability, a palette colour as-is. */
  nameColor?: string;
  /** Resolved domain, or undefined when the inference abstained. */
  domain?: string;
  /** The run's untracked co-subjects, for the monopoly disclosure. */
  untrackedRivals: readonly { name: string; norm?: string; unitCount?: number }[];
  /**
   * Whether this purpose measures share of voice.
   *
   * A prompt that names only the customer's brand hands it nearly the whole answer by construction,
   * so its bar and percentage restate the question. The mention itself still belongs in the list —
   * who the answer named is not a KPI — but the proportion beside it is not a measurement.
   */
  showShareOfVoice: boolean;
  /** Whether this purpose measures sentiment. */
  showSentiment: boolean;
  onSpotlight: () => void;
  onClearSpotlight: () => void;
  /** Pins this row's passages, or releases them when it is already pinned. */
  onToggleSelected: () => void;
  selected: boolean;
}) {
  const { termStatus } = useTaxonomyTermName(api, mentionTermKey(entity) ?? '');
  const presentation = termStatusPresentation(termStatus, 'table-row');

  if (presentation.hidden) {
    return null;
  }

  const ownership = classifyEntityOwnership(entity.type);
  const sov = shareOfVoicePercent(entity.shareOfVoice);
  const barColor = mutedColorFor(color || '#94a3b8', presentation);

  return (
    <div
      className={[SPOTLIGHT_ROW_CLASS_NAME, spotlightRowStateClassName(selected), 'text-sm', presentation.className]
        .filter(Boolean)
        .join(' ')}
      title={presentation.title}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onMouseEnter={onSpotlight}
      onMouseLeave={onClearSpotlight}
      onClick={onToggleSelected}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggleSelected();
        }
      }}
    >
      <EntityFavicon api={api} domain={domain} />
      <span
        className={`min-w-[8rem] truncate ${ownership === 'own' ? 'font-bold' : ''}`}
        // A stored colour is darkened so the name stays legible on a near-white row; a palette
        // colour is left as it reads today, and the bar elsewhere uses the raw value.
        style={{ color: nameColor || '#334155' }}
      >
        {entity.name}
        {presentation.showSuggestionIcon ? <SuggestedTermIcon /> : null}
      </span>
      {showShareOfVoice ? (
        <div className="flex-grow-1 h-2 rounded bg-slate-100 overflow-hidden">
          <div className="h-full" style={{ width: `${sov}%`, backgroundColor: barColor }} />
        </div>
      ) : (
        // Takes the bar's place in the flex line so the names stay left-aligned and the trailing
        // figures stay right. It is NOT the same size as the bar — it drops the `h-2` and has no
        // height of its own. The row keeps its height regardless, because it is `flex items-center`
        // and is sized by its text, which is taller than the 8px bar ever was.
        <div className="flex-grow-1" />
      )}
      <EntityShareAndSentiment
        sovPercent={sov}
        showShare={showShareOfVoice}
        tone={showSentiment ? toneForRow(entity) : undefined}
        percentClassName="text-slate-500 w-10 text-right"
      />
      {/* Sits AT the misleading figure, not with the list below it: a 100% share is correct and
          still misleading when the answer named rivals nobody is counting. Gone with the figure it
          qualifies. */}
      {showShareOfVoice ? <UntrackedRivalsChip api={api} sharePercent={sov} rivals={untrackedRivals} /> : null}
    </div>
  );
}

/**
 * One legend chip above the answer: a colour swatch and the brand it tints.
 *
 * Two independent kinds of dimming meet here and must stay distinguishable. `hidden` is the
 * reader's own toggle — they switched this brand's highlighting off, shown as a hollow swatch and a
 * struck-through name. Status muting is about the term's curation state and shows as reduced
 * opacity across swatch and name together, because a full-strength swatch beside a faded name reads
 * as a rendering glitch rather than a status.
 */
function LegendChip({
  api,
  item,
  hidden,
  domain,
  onToggle,
}: {
  api?: ISyncCoreApi;
  item: LegendEntry;
  hidden: boolean;
  /** Resolved domain, or undefined when the inference abstained. */
  domain?: string;
  onToggle: () => void;
}) {
  const { termStatus } = useTaxonomyTermName(api, item.termKeys[0] ?? '');
  const presentation = termStatusPresentation(termStatus, 'legend');

  if (presentation.hidden) {
    return null;
  }

  return (
    <button
      type="button"
      className={[
        'inline-flex items-center gap-1.5 rounded-md border-0 bg-transparent px-1.5 py-0.5 transition-colors duration-150 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        presentation.className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onToggle}
      aria-pressed={!hidden}
      title={presentation.title ?? (hidden ? `Highlight ${item.label}` : `Stop highlighting ${item.label}`)}
    >
      <span
        aria-hidden
        className="inline-block h-2.5 w-2.5 rounded-[3px]"
        // The brand's own colour, known only at runtime; hollow while the reader has switched it off.
        style={{ backgroundColor: hidden ? 'transparent' : item.color, border: `1px solid ${item.color}` }}
      />
      <EntityFavicon api={api} domain={domain} />
      <span className={hidden ? 'text-slate-400 line-through' : 'text-slate-600'}>{item.label}</span>
      {presentation.showSuggestionIcon ? <SuggestedTermIcon /> : null}
    </button>
  );
}

export function GovernanceAiAnswerDetail({ runId }: { runId: string }) {
  const apiContext = useContext(SyncCoreApiContext);
  const api = apiContext?.api;
  // The list's filters travel on the address the host page keeps, handed over with the path; the
  // frame's own address only fills the moment right after the in-frame navigation that opened this
  // page. Back and Prev/Next carry this search on, so the list reopens as the reader left it.
  const screenAddress = useBrandPresenceScreenAddress();
  const screenSearch = searchOfBrandPresenceScreenAddress(screenAddress);
  const navigate = useNavigate();

  const ownColor = useMemo(() => readPrimaryColor(), []);

  /** Locale term keys that identify English, from the cached locale taxonomy. */
  const [englishLocaleKeys, setEnglishLocaleKeys] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!api) {
      return;
    }
    let active = true;
    void getCachedTaxonomyTermsByFacet(api, TaxonomyFacet.Locale)
      .then((terms) => {
        if (!active) {
          return;
        }
        const keys = new Set<string>();
        for (const term of terms as { key?: string; name?: string }[]) {
          const key = String(term.key || '').trim();
          if (key && isEnglishLocaleName(term.name || key)) {
            keys.add(key);
          }
        }
        setEnglishLocaleKeys(keys);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [api]);

  /** The locale Sync Core should translate into. Empty when the project tracks no English locale. */
  const englishLocaleKey = useMemo(() => [...englishLocaleKeys][0] ?? '', [englishLocaleKeys]);
  const englishLocaleKeyRef = React.useRef(englishLocaleKey);
  englishLocaleKeyRef.current = englishLocaleKey;

  /**
   * The purpose the reader arrived with, from the same URL parameter the answers list writes.
   *
   * The entity-domain endpoint is scoped to one purpose, and this page is reached from a
   * purpose-filtered list, so the parameter is the honest source: it is the purpose whose answers
   * the reader is actually reading. Landing here without one (a bare deep link) resolves no
   * favicons rather than picking a purpose on the reader's behalf.
   */
  const purposeTermKey = useMemo(
    () => parseAiAnswersFilterState(screenSearch, screenAddress.query).purposeTermKey,
    [screenSearch, screenAddress]
  );
  const entityDomains = useEntityDomains(api, purposeTermKey);

  /**
   * The purpose tag behind that key, which is what says which KPIs this answer's numbers are.
   *
   * Read from the same cached per-facet fetch every other term on this page resolves through, so it
   * costs no request of its own. A bare deep link carrying no purpose resolves no tag and therefore
   * no mask — the page then renders exactly what it renders today, because nothing has stated what
   * these prompts are for.
   */
  const [purposeTerms, setPurposeTerms] = useState<PurposeTermLike[]>([]);
  useEffect(() => {
    if (!api) {
      return;
    }
    let active = true;
    void getCachedTaxonomyTermsByFacet(api, TaxonomyFacet.MonitoringPromptPurpose)
      .then((terms) => {
        if (active) {
          setPurposeTerms(terms);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [api]);

  const purposeTerm = useMemo(
    () => (purposeTermKey ? purposeTerms.find((term) => String(term?.key ?? '').trim() === purposeTermKey) : undefined),
    [purposeTerms, purposeTermKey]
  );
  const kpiMask = useMemo(() => purposeKpiMask(purposeTerm), [purposeTerm]);
  const showsPosition = isKpiShown(kpiMask, MonitoringPromptKpi.Position);
  const showsShareOfVoice = isKpiShown(kpiMask, MonitoringPromptKpi.ShareOfVoice);
  const showsSentiment = isKpiShown(kpiMask, TONE_KPI);
  const showsRecommendation = isKpiShown(kpiMask, MonitoringPromptKpi.Recommendation);
  const showsAttributes = isKpiShown(kpiMask, MonitoringPromptKpi.Attributes);

  // Same shared resolution the answers list and the Analytics module use, so a source reads
  // identically wherever the reader meets it.
  const { index: authoritySources, ready: sourcesReady } = useAuthoritySources(api);
  // Seeded from the link the reader arrived on, so the grouping they chose in the list carries
  // into the answer they opened.
  const [sourceGrouping, setSourceGrouping] = useState<SourceGrouping>(
    () => parseAiAnswersFilterState(screenSearch, screenAddress.query).sourceGrouping
  );

  const [run, setRun] = useState<ClientMonitoringPromptRunEntity | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [markdown, setMarkdown] = useState<string>('');
  const [parsed, setParsed] = useState<MonitoringPromptRunParsed | undefined>(undefined);
  /**
   * True when the parsed document could not be fetched.
   *
   * Tracked separately from the page error because the parsed file is genuinely best-effort now:
   * the header, the sidebar and the collapsed cited-source rows all come from the run document, so
   * losing the parsed file costs the answer body, the per-page source links and the attribute
   * verdicts read from the passages — not the page.
   */
  const [parsedFailed, setParsedFailed] = useState(false);
  const [promptDisplay, setPromptDisplay] = useState<PromptDisplay | undefined>(undefined);
  // Same control and same deep-link param as the list, so the choice survives the round trip.
  const [promptLanguage, setPromptLanguage] = useState<PromptLanguageMode>(
    () => parseAiAnswersFilterState(screenSearch, screenAddress.query).promptLanguage
  );
  const questionText = pickPromptText(promptDisplay, promptLanguage);
  const [highlightBrands, setHighlightBrands] = useState(true);
  /** Legend labels the reader has switched off. Empty = every brand highlighted. */
  const [mutedBrands, setMutedBrands] = useState<Set<string>>(new Set());
  const [colouring, setColouring] = useState<PassageColouring>('brand');
  /** The passage under the pointer or the keyboard focus, and the one the reader pinned. */
  const [hoveredPassageId, setHoveredPassageId] = useState<string | undefined>(undefined);
  const [pinnedPassageId, setPinnedPassageId] = useState<string | undefined>(undefined);
  const [prevRunId, setPrevRunId] = useState<string | undefined>(undefined);
  const [nextRunId, setNextRunId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!api) {
      return;
    }

    let active = true;
    setLoading(true);
    setError(undefined);
    setMarkdown('');
    setParsed(undefined);
    setParsedFailed(false);
    // A passage belongs to one answer; moving to the next answer lets go of it.
    setHoveredPassageId(undefined);
    setPinnedPassageId(undefined);

    void (async () => {
      try {
        const loadedRun = await api.governance.monitoringPromptRuns.item(runId as unknown as InternalId);
        if (!active) {
          return;
        }
        setRun(loadedRun);
        setLoading(false);

        // The run's OWN question text for the header, read from the revision that produced this
        // answer. `getRevision()` loads that revision by id; the sibling `get()` would load the
        // prompt by key, and the client pins that lookup to the Active status — so a paused or
        // archived prompt would resolve to nothing and this header would read "Unknown prompt".
        // The by-id path carries no status filter, and it shows the question as it was actually
        // asked rather than however the prompt has been edited since. Do not swap it for `get()`.
        //
        // Its English counterpart is resolved by Sync Core and fetched separately below, because
        // the high-level `item()` client method does not forward query params: as of rest-client
        // 4.135.1 it is still typed `(id, skipCache?)`, so unlike `list()` — which now declares
        // `promptTranslationLocaleKey` — there is no parameter to pass it through. Closing that
        // gap belongs in the generated client, not here.
        void loadedRun.prompt
          .getRevision()
          .then((prompt) => {
            if (active) {
              setPromptDisplay({ local: promptLabelOf(prompt) });
            }
          })
          .catch(() => {
            if (active) {
              setPromptDisplay({ local: UNKNOWN_PROMPT_LABEL });
            }
          });

        // Server-resolved translation for this one run. Best-effort: when it is absent the header
        // keeps the run's own language and shows the unresolved marker — never a guess.
        if (englishLocaleKeyRef.current) {
          void (api as unknown as { services: { api: { monitoringPromptRunControllerItem: Function } } }).services.api
            .monitoringPromptRunControllerItem({ id: runId, promptTranslationLocaleKey: englishLocaleKeyRef.current })
            .then((raw: { promptTranslations?: unknown }) => {
              const english = translationTextFor(raw?.promptTranslations, englishLocaleKeyRef.current);
              if (active && english) {
                setPromptDisplay((current) => ({ local: current?.local ?? UNKNOWN_PROMPT_LABEL, english }));
              }
            })
            .catch(() => undefined);
        }

        // The parsed answer document, fetched ONCE per run and shared by everything on this page
        // that needs it — the answer body, the tinting, the attribute verdicts and the per-page links
        // under a cited source. A second fetch for the sources block would download the same file twice.
        //
        // Genuinely best-effort: its failure is caught here rather than by the outer handler, so a
        // missing parsed file can no longer blank a page whose run document loaded fine.
        try {
          const file = await loadedRun.parsedFile?.get?.();
          if (!active) {
            return;
          }
          if (!file?.downloadUrl) {
            setParsedFailed(true);
            return;
          }
          const response = await fetch(file.downloadUrl);
          const content = await response.text();
          if (!active) {
            return;
          }
          const parsedRuntime = plainToInstance(MonitoringPromptRunParsed, JSON.parse(content));
          setParsed(parsedRuntime);
          setMarkdown(parsedRuntime.markdown || '');
        } catch {
          if (active) {
            setParsedFailed(true);
          }
        }
      } catch (loadError) {
        if (active) {
          setError('Failed to load this AI answer.');
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, runId]);

  // The locale display name, resolved through the shared term index — never the raw key. It reads
  // the same single fetch every other term on this page resolves through, so it costs no request
  // of its own; an unresolved key stays blank exactly as it did before.
  const localeTermKey = run ? referenceKey(run.locale) : '';
  const { termName: resolvedLocaleName, loading: localeNameLoading } = useTaxonomyTermName(api, localeTermKey);
  const localeName = localeTermKey && !localeNameLoading && resolvedLocaleName !== localeTermKey ? resolvedLocaleName : '';

  // Prev/Next navigation across the current filtered list (same-page neighbours).
  useEffect(() => {
    if (!api) {
      return;
    }

    let active = true;
    void (async () => {
      try {
        const state = parseAiAnswersFilterState(screenSearch, screenAddress.query);
        const listFilters = buildRunListFilters(state);
        const response = await api.governance.monitoringPromptRuns.list(
          { page: state.page, itemsPerPage: 25 },
          listFilters as { status?: MonitoringPromptRunStatus[]; promptKey?: string }
        );
        if (!active) {
          return;
        }
        const items = response.items || [];
        const index = items.findIndex((item) => String(item.id) === runId);
        setPrevRunId(index > 0 ? String(items[index - 1].id) : undefined);
        setNextRunId(index >= 0 && index < items.length - 1 ? String(items[index + 1].id) : undefined);
      } catch {
        if (active) {
          setPrevRunId(undefined);
          setNextRunId(undefined);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, runId, screenSearch, screenAddress]);

  // "Who's mentioned" must only list entities that were actually mentioned. `run.entities[]`
  // also carries the tracked entities the answer did NOT mention (`matchType: 'missing'`, share
  // 0, no sentiment) — rendering those produced the reported rows of "name · 0% · —" with an
  // empty bar under a heading that promises the opposite.
  const rankedEntities = useMemo(() => {
    return (run?.entities || [])
      .filter((entity) => VISIBLE_MENTION_FACETS.includes(entity.type) && entity.matchType !== MonitoringPromptRunEntityMatchType.Missing)
      .slice()
      .sort((a, b) => shareOfVoicePercent(b.shareOfVoice) - shareOfVoicePercent(a.shareOfVoice));
  }, [run]);

  /**
   * The alternatives this answer named that the customer does not track.
   *
   * They come from the run's own `untrackedCoSubjects` mirror, so this costs nothing beyond the run
   * already loaded — no parsed file, no second request. Before the mirror existed the operator UI
   * could say a suggestion had been seen in four answers but could not show which ones, and this
   * box listed only tracked brands while the answer plainly discussed others.
   *
   * Ordered by how many units named each one, because that is the same measure the suggestion
   * pipeline scores them by: the name at the top of this list is the one most likely to be worth
   * tracking.
   */
  const untrackedCoSubjects = useMemo(() => {
    return (run?.untrackedCoSubjects || [])
      .filter((entry) => !!entry?.name?.trim())
      .slice()
      .sort((left, right) => (right.unitCount || 0) - (left.unitCount || 0) || left.name.localeCompare(right.name));
  }, [run]);

  // ONE colour assignment serves the whole page: the answer tints, the legend, "Who's mentioned",
  // the attributes table and the cited-source attributions all read it, so a competitor is the
  // same colour wherever the reader meets it.
  // The parsed mentions are folded in as a second source for the term join: `run.entities[].terms`
  // is optional on the contract, and without those keys the term-keyed colour map comes out empty
  // and the whole answer renders untinted.
  // Keyed on the map's INPUTS, not the map it returns: the stored colours feed into the build, so
  // deriving their keys from the built map would be circular.
  const storedColorTermKeys = useMemo(
    () => collectColorableTermKeys(rankedEntities, parsed?.mentions as ColorableMention[] | undefined),
    [rankedEntities, parsed]
  );
  const storedTermColors = useTermColors(api, storedColorTermKeys);
  const rawEntityColors = useMemo(
    () =>
      buildEntityColorMap(
        rankedEntities,
        ownColor,
        COMPETITOR_PALETTE,
        parsed?.mentions as ColorableMention[] | undefined,
        storedTermColors
      ),
    [rankedEntities, ownColor, parsed, storedTermColors]
  );

  // The answer body's tints are keyed by term but painted far from any name, so a suggested term's
  // colour has to be muted before it reaches the painter — there is nowhere downstream to do it.
  const colorTermKeys = useMemo(() => Array.from(rawEntityColors.byTermKey.keys()), [rawEntityColors]);
  const termStatuses = useTermStatuses(api, colorTermKeys);
  const entityColors = useMemo(() => applyTermStatusToColorMap(rawEntityColors, termStatuses), [rawEntityColors, termStatuses]);
  const legend = useMemo(() => buildEntityLegend(entityColors), [entityColors]);

  const isMuted = (label: string) => mutedBrands.has(label);
  const toggleBrand = (label: string) =>
    setMutedBrands((current) => {
      const next = new Set(current);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });

  /** The legend label an entity belongs to — own entities all sit under the single "You" chip. */
  const legendLabelFor = (ownership: EntityOwnership, name: string) => (ownership === 'own' ? 'You' : name);

  // Muting a legend chip drops that brand's tint AND its name highlight, so the toggle reads as
  // "stop highlighting this brand" rather than only dimming one of the two layers.
  const colorByTermKey = useMemo(() => {
    if (!mutedBrands.size) {
      return entityColors.byTermKey;
    }
    const active = new Map<string, string>();
    for (const entry of entityColors.entries) {
      if (isMuted(legendLabelFor(entry.ownership, entry.name))) {
        continue;
      }
      for (const termKey of entry.termKeys) {
        active.set(termKey, entry.color);
      }
    }
    return active;
  }, [entityColors, mutedBrands]);

  const matchers = useMemo(() => {
    const nameMatchers: BrandMatcher[] = [];
    for (const entity of rankedEntities) {
      const ownership = classifyEntityOwnership(entity.type);
      const color = colorForEntityName(entityColors, entity.name);
      if (color && entity.name && entity.name.length > 1 && !isMuted(legendLabelFor(ownership, entity.name))) {
        nameMatchers.push({
          name: entity.name,
          color,
          textColor: readableEntityTextColor(entityColors, color, { name: entity.name }),
          ownership,
        });
      }
    }
    // Longest first, so "Acme Pro" wins over "Acme" when both are highlighted.
    return nameMatchers.sort((a, b) => b.name.length - a.name.length);
  }, [rankedEntities, entityColors, mutedBrands]);

  /** Term key -> entity display name, for the passage hover tooltip. */
  const nameByTermKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of entityColors.entries) {
      const label = entry.ownership === 'own' ? 'You' : entry.name;
      for (const termKey of entry.termKeys) {
        if (!map.has(termKey)) {
          map.set(termKey, label);
        }
      }
    }
    return map;
  }, [entityColors]);

  /**
   * Subjects stay lit when the reader CLICKS their rows, and one lights transiently while hovered.
   *
   * Two independent states, not one: hover alone cannot hold a passage lit, since the highlight
   * would die the instant the cursor moved and a reader could not point at a passage, scroll to
   * it, or compare it against anything without losing it. Clicking pins the subject instead; the
   * cursor is then free.
   *
   * The pins are a SET, not a slot. Pinning a second brand leaves the first pinned, so a reader can
   * hold two brands and an attribute lit at once and read how they overlap in the answer.
   */
  const [selectedSubjects, setSelectedSubjects] = useState<SpotlightSubject[]>([]);
  const [hoveredSubject, setHoveredSubject] = useState<SpotlightSubject | undefined>(undefined);

  /**
   * The passages a subject covers.
   *
   * Resolved at render rather than when the row is clicked, so a subject pinned before the parsed
   * document arrives lights up as soon as it does instead of having resolved to nothing.
   */
  const targetsFor = React.useCallback(
    (subject: SpotlightSubject | undefined): SpotlightTargets | undefined => {
      if (!subject) {
        return undefined;
      }

      if (subject.kind === 'aspect') {
        // An attribute is a property of whole units, so it has no per-cell granularity.
        return {
          lines: lineIndicesForUnits(
            parsed?.units as PaintUnit[] | undefined,
            unitIdsForAspectTerm(parsed?.aspects as SpotlightAspect[] | undefined, subject.termKey)
          ),
          cells: new Map(),
        };
      }

      const entry = entityColors.entries.find((candidate) => candidate.name === subject.name);
      return spotlightTargets(
        parsed?.units as PaintUnit[] | undefined,
        parsed?.labels as PaintLabel[] | undefined,
        parsed?.resolution as PaintResolution[] | undefined,
        entry?.termKeys
      );
    },
    [parsed, entityColors]
  );

  // The union over every pinned subject plus the hovered one, so a second pin ADDS to the first and
  // hovering adds to both — which is what makes holding several subjects side by side possible.
  const spotlight = React.useMemo(
    () => unionSpotlightTargets([...selectedSubjects.map(targetsFor), targetsFor(hoveredSubject)]),
    [targetsFor, selectedSubjects, hoveredSubject]
  );

  // An empty result is a real answer — "this subject is not attributable to any passage" — and is
  // kept as an empty set so the answer dims. Collapsing it to undefined made a failed lookup look
  // identical to no hover at all, i.e. everything highlighted.
  const spotlightLines = spotlight?.lines;
  const spotlightCells = spotlight?.cells;

  /** Clicking a row adds it to the pinned set; clicking a pinned row releases just that one. */
  const toggleSelectedSubject = (subject: SpotlightSubject) => setSelectedSubjects((current) => toggleSpotlightSubject(current, subject));

  const paintByLine = useMemo(
    () =>
      buildLinePaint(
        markdown,
        parsed?.units as PaintUnit[] | undefined,
        parsed?.labels as PaintLabel[] | undefined,
        parsed?.resolution as PaintResolution[] | undefined,
        colorByTermKey
      ),
    [markdown, parsed, colorByTermKey]
  );

  // Present only once the extraction persists per-cell credit; absent documents keep the
  // row-level rendering, in which a multi-subject comparison row stays neutral.
  const cellPaintByLine = useMemo(
    () => buildCellPaint(parsed?.units as PaintUnit[] | undefined, parsed?.labels as PaintLabel[] | undefined, colorByTermKey),
    [parsed, colorByTermKey]
  );

  // "Attributes in this answer" — rows and bars from the server-computed per-(entity, attribute)
  // rollup on the run, where `share` is the coverage percentage; each cell's verdict comes from the
  // passages it points at (`attributeVerdicts` below). Absent aspectScores means "not measured" —
  // the section simply does not render (never 0%).
  const attributeGroups = useMemo(() => buildAttributeGroups(run?.aspectScores as AttributeScoreLike[] | undefined), [run]);
  // Resolved once for the table so each row can mute (or drop) as a unit — and so the cell
  // tooltips can NAME the attribute instead of calling it "this attribute". One resolution serves
  // both; a second hook for the names would refetch the same terms.
  const { statusOf: attributeStatusOf, nameOf: attributeNameOf } = useTermRowVisibility(
    api,
    useMemo(() => attributeGroups.map((group) => group.aspectTermKey), [attributeGroups])
  );

  // Column order for the attributes table: entities ranked by their total measured share, so the
  // most-discussed entity leads. Derived from the same aspectScores rows the bars read.
  const attributeEntityKeys = useMemo(() => {
    const shareByEntity = new Map<string, number>();
    for (const group of attributeGroups) {
      for (const entry of group.entries) {
        if (entry.entityKey) {
          shareByEntity.set(entry.entityKey, (shareByEntity.get(entry.entityKey) || 0) + entry.share);
        }
      }
    }
    return Array.from(shareByEntity.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([entityKey]) => entityKey);
  }, [attributeGroups]);

  /**
   * The full width of every attribute bar: the largest share among the cells the table draws, so the
   * most-discussed attribute runs the width and one mentioned in passing still shows. Rows an
   * attribute's status hides are left out, or the widest bar on screen could stop short of the edge.
   */
  const attributeCeiling = useMemo(
    () =>
      attributeShareCeiling(
        attributeGroups.filter((group) => !termStatusPresentation(attributeStatusOf(group.aspectTermKey), 'table-row').hidden),
        attributeEntityKeys
      ),
    [attributeGroups, attributeEntityKeys, attributeStatusOf]
  );

  /**
   * Which terms are the customer's own, so "You" leads its source row.
   *
   * Derived from the very colour map the answer body is painted with, so the ordering rule and the
   * colours can never disagree about which entity is you.
   */
  const ownTermKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const entry of entityColors.entries) {
      if (entry.ownership === 'own') {
        entry.termKeys.forEach((termKey) => keys.add(termKey));
      }
    }
    return keys;
  }, [entityColors]);

  /** The answer passage by passage — empty until the analysed answer arrives, or when it has none. */
  const passageIndex = useMemo(() => buildAnswerPassageIndex(parsed), [parsed]);
  /** Whether the analysed answer is still on its way, so what is read from it has yet to arrive. */
  const parsedLoading = !parsed && !parsedFailed;

  // The passages the answer draws with a card, read by the rules the answer is drawn by; and each
  // brand's verdict on each attribute, read from exactly the drawn passages a click on the attribute
  // lights for that brand — so a cell reads what those passages' cards read.
  const passageElements = useMemo(() => answerPassageElements(markdown, passageIndex), [markdown, passageIndex]);
  const attributeVerdicts = useMemo(
    () => buildAttributeVerdicts(parsed, passageIndex, passageElements),
    [parsed, passageIndex, passageElements]
  );

  // Offered only where there are passages to shade and the purpose measures the verdict at all.
  const verdictColouringAvailable = showsSentiment && passageIndex.order.length > 0;
  const colouredByVerdict = colouring === 'verdict' && verdictColouringAvailable;

  // Every brand's verdict at once, so the brands can be compared passage by passage.
  const verdictLens = useMemo(
    () => (colouredByVerdict ? buildVerdictLens(markdown, passageIndex) : undefined),
    [colouredByVerdict, markdown, passageIndex]
  );

  // Leaving a passage closes its card only after a moment, so crossing the gap to the next one does
  // not blink the card out and back; entering any passage cancels the pending close.
  const hoverReleaseTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hoverPassage = React.useCallback((unitId: string | undefined) => {
    if (hoverReleaseTimer.current) {
      clearTimeout(hoverReleaseTimer.current);
      hoverReleaseTimer.current = undefined;
    }
    if (unitId) {
      setHoveredPassageId(unitId);
      return;
    }
    hoverReleaseTimer.current = setTimeout(() => {
      hoverReleaseTimer.current = undefined;
      setHoveredPassageId(undefined);
    }, PASSAGE_HOVER_RELEASE_MS);
  }, []);
  useEffect(
    () => () => {
      if (hoverReleaseTimer.current) {
        clearTimeout(hoverReleaseTimer.current);
      }
    },
    []
  );

  const togglePinnedPassage = React.useCallback(
    (unitId: string) => setPinnedPassageId((current) => (current === unitId ? undefined : unitId)),
    []
  );
  const unpinPassage = React.useCallback(() => setPinnedPassageId(undefined), []);

  /** The answer's grid, which the passage card is laid over and measured against, and the sidebar's column in it. */
  const answerGridRef = React.useRef<HTMLDivElement>(null);
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  // Letting go from inside the card by keyboard closes the card under the focus, so the focus goes
  // back to the passage rather than to nowhere — without scrolling: the passage is where the card was.
  // A pointer's click just closes the card.
  const unpinFromCard = React.useCallback(
    (byKeyboard: boolean) => {
      const passage =
        byKeyboard && pinnedPassageId && answerGridRef.current ? findPassageElement(answerGridRef.current, pinnedPassageId) : undefined;
      setPinnedPassageId(undefined);
      passage?.focus({ preventScroll: true });
    },
    [pinnedPassageId]
  );

  // Escape lets go of a pinned passage from anywhere on the page, not only from the passage itself.
  useEffect(() => {
    if (!pinnedPassageId) {
      return undefined;
    }
    const release = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      const focused = document.activeElement;
      if (focused instanceof Element && focused.closest('[data-passage-card-for]')) {
        unpinFromCard(true);
      } else {
        setPinnedPassageId(undefined);
      }
    };
    window.addEventListener('keydown', release);
    return () => window.removeEventListener('keydown', release);
  }, [pinnedPassageId, unpinFromCard]);

  const passageInteraction = useMemo<PassageInteraction | undefined>(
    () =>
      passageIndex.order.length
        ? {
            passages: passageIndex,
            activeId: hoveredPassageId,
            pinnedId: pinnedPassageId,
            onHover: hoverPassage,
            onToggle: togglePinnedPassage,
            onUnpin: unpinPassage,
          }
        : undefined,
    [passageIndex, hoveredPassageId, pinnedPassageId, hoverPassage, togglePinnedPassage, unpinPassage]
  );

  const passageMeasures = useMemo<PassageCardMeasures>(
    () => ({ verdict: showsSentiment, position: showsPosition, recommendation: showsRecommendation, attributes: showsAttributes }),
    [showsSentiment, showsPosition, showsRecommendation, showsAttributes]
  );
  const brandNameOf = React.useCallback((termKey: string) => nameByTermKey.get(termKey), [nameByTermKey]);
  const brandColorOf = React.useCallback((termKey: string) => entityColors.byTermKey.get(termKey), [entityColors]);
  const renderPassageCard = React.useCallback(
    (unitId: string) => {
      const passage = passageIndex.byId.get(unitId);
      return passage ? (
        <AnswerPassageCard
          api={api}
          passage={passage}
          excerpt={passageExcerpt(markdown, passage)}
          pinned={passage.unitId === pinnedPassageId}
          onUnpin={unpinFromCard}
          nameOf={brandNameOf}
          colorOf={brandColorOf}
          measures={passageMeasures}
        />
      ) : null;
    },
    [api, passageIndex, markdown, pinnedPassageId, unpinFromCard, brandNameOf, brandColorOf, passageMeasures]
  );

  const winner = run ? resolveWinner(run.entities, run.explicitRecommendation?.evidence) : undefined;

  // Answer-grain placing of the customer's own subject. Undefined when this answer never
  // positioned it, which the card reports rather than filling in.
  const targetPosition = useMemo(() => targetPositionInAnswer(run?.entities), [run]);
  // Historical runs carry a leaked unit-id marker in the persisted evidence, and the evidence is
  // usually prose wrapped around the actual quotation — show only what the answer said.
  const evidenceQuote = formatRecommendationEvidence(winner?.evidence);

  const goBack = () => navigate(`/brand-presence/analytics/ai-answers${screenSearch}`);

  if (loading && !run) {
    return (
      <SkeletonRegion label="Loading answer…" className="w-full h-full overflow-hidden bg-slate-50 px-6 py-8">
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-4 flex justify-between">
            <SkeletonBlock className="h-8 w-40 rounded-lg" />
            <SkeletonBlock className="h-8 w-36 rounded-lg" />
          </div>
          <div className={`${BP_CARD_CLASSES} mb-4 space-y-3 p-6`}>
            <SkeletonBlock className="h-3 w-40" />
            <SkeletonBlock className="h-6 w-2/3 rounded-lg" />
            <div className="flex gap-2">
              <SkeletonBlock className="h-6 w-24 rounded-full" />
              <SkeletonBlock className="h-4 w-40" />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="space-y-3 lg:col-span-2">
              <div className={`${ANSWER_TOOLBAR_CLASSES} h-11`}>
                <SkeletonBlock className="h-3 w-16" />
                <SkeletonBlock className="h-3 w-40" />
                <SkeletonBlock className="ml-auto h-6 w-48 rounded-lg" />
              </div>
              <div className={`${BP_ANSWER_SURFACE_CLASSES} space-y-3`}>
                {ANSWER_SKELETON_LINE_WIDTHS.map((lineClassName, line) => (
                  <SkeletonBlock key={line} className={lineClassName} />
                ))}
              </div>
            </div>
            <div className="space-y-4">
              {[0, 1, 2].map((card) => (
                <div key={card} className={`${BP_CARD_CLASSES} space-y-3 p-4`}>
                  <SkeletonBlock className="h-2.5 w-24" />
                  <SkeletonBlock className="h-5 w-40" />
                  <SkeletonBlock className="h-3 w-full" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </SkeletonRegion>
    );
  }

  if (error || !run) {
    return (
      <div className="w-full h-full bg-slate-50 px-6 py-8">
        <button type="button" className={`${BP_TOOLBAR_BUTTON_SMALL_CLASSES} mb-4`} onClick={goBack}>
          ‹ Back to AI answers
        </button>
        <div className={`${BP_CARD_CLASSES} px-5 py-4 text-sm text-rose-600`}>{error || 'Answer not found.'}</div>
      </div>
    );
  }

  const promptKey = referenceKey(run.prompt);
  const engineKey = referenceKey(run.engine);
  const country = translateCountry(run.country);

  // Every favicon on this page (the entity badges, and AnswerCitedSources' own) waits for this: the
  // run itself — entities, citations and all — has landed, not a timer. `loading` goes true again on
  // Prev/Next while the outgoing run's content is still on screen, which correctly re-defers a
  // not-yet-requested icon without touching one already shown (see `FaviconLoadGate`).
  const faviconLoadReady = !loading;

  return (
    <FaviconLoadGate ready={faviconLoadReady}>
      <div className="relative w-full h-full bg-slate-50 text-slate-900 bg-brand-page">
        <main className="relative max-w-[1600px] mx-auto px-6 py-8">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <button type="button" className={BP_TOOLBAR_BUTTON_SMALL_CLASSES} onClick={goBack}>
              ‹ Back to AI answers
            </button>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                disabled={!prevRunId}
                title={prevRunId ? 'Previous answer in the filtered list' : 'No previous answer on this page of the filtered list'}
                onClick={() => prevRunId && navigate(`/brand-presence/analytics/ai-answers/${prevRunId}${screenSearch}`)}
              >
                ‹ Prev
              </button>
              <button
                type="button"
                className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                disabled={!nextRunId}
                title={nextRunId ? 'Next answer in the filtered list' : 'No next answer on this page of the filtered list'}
                onClick={() => nextRunId && navigate(`/brand-presence/analytics/ai-answers/${nextRunId}${screenSearch}`)}
              >
                Next ›
              </button>
            </div>
          </div>

          <div className={`${BP_CARD_CLASSES} mb-4 p-6 motion-safe:animate-bp-fade-up`}>
            <BrandPresenceEyebrow>
              Brand Presence <span className="text-slate-300">/</span> AI answer
            </BrandPresenceEyebrow>
            <div className="d-flex align-items-start justify-content-between gap-3">
              <h1 className="m-0 mb-2 text-xl font-bold leading-snug tracking-tight text-slate-900 [text-wrap:balance]">
                {shouldMarkOriginalLanguage(promptDisplay, promptLanguage) ? (
                  <FontAwesomeIcon
                    icon={faGlobe}
                    className="text-slate-400 me-2 text-sm align-middle"
                    title={ORIGINAL_LANGUAGE_HINT}
                    aria-label={ORIGINAL_LANGUAGE_HINT}
                  />
                ) : null}
                {questionText}
              </h1>
              {englishLocaleKey && promptDisplay?.english && promptDisplay.english !== promptDisplay.local ? (
                // Only offered when there is actually another language to switch to.
                <label
                  className="mb-0 flex shrink-0 cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-500"
                  title={PROMPT_LANGUAGE_HINT}
                >
                  <input
                    type="checkbox"
                    checked={promptLanguage === 'local'}
                    onChange={(event) => setPromptLanguage(event.target.checked ? 'local' : 'english')}
                  />
                  Original language
                </label>
              ) : null}
            </div>
            <div className="text-sm tabular-nums text-slate-500 d-flex align-items-center gap-2 flex-wrap">
              {engineKey ? (
                <TaxonomyTermNameBadge api={api} termKey={engineKey} applyFacetPresentation termFacet={TaxonomyFacet.Engine} />
              ) : null}
              {country ? <span>· {country}</span> : null}
              {localeName ? <span>· {localeName}</span> : null}
              <span>· {run.scheduledFor?.format('MMM DD, YYYY HH:mm')}</span>
            </div>
          </div>

          {isPendingRunStatus(run.status) ? (
            // Nothing has been measured yet, so none of the analysis panels may be shown — an empty
            // answer body with empty sidebar sections reads as "we found nothing", which is a claim.
            <div className={`${BP_CARD_CLASSES} p-10 text-center motion-safe:animate-bp-fade-up`}>
              <div className="mb-1 text-base font-bold text-slate-900">This answer is still being retrieved and analyzed</div>
              <div className="text-sm text-slate-500">
                Check back soon — status: <MonitoringPromptRunStatusBadge status={run.status} />
              </div>
            </div>
          ) : (
            // Positioned only so the passage card can be laid over it; nothing inside is placed against it.
            <div ref={answerGridRef} className="group/answer grid grid-cols-1 lg:grid-cols-3 gap-4 relative">
              {/* Answer (reading experience) */}
              <div className="lg:col-span-2 space-y-3">
                {/* One toolbar above the answer: its heading, the key to its colours, and the controls
                  that change them. */}
                <div className={ANSWER_TOOLBAR_CLASSES}>
                  <h2 className="m-0 font-eyebrow text-[0.6875rem] font-medium uppercase tracking-[0.16em] text-slate-500">Answer</h2>
                  {highlightBrands && !(colouredByVerdict && verdictLens) && legend.length ? (
                    <span aria-hidden className="hidden h-4 w-px bg-slate-200 sm:inline-block" />
                  ) : null}
                  {highlightBrands && colouredByVerdict && verdictLens ? (
                    // The verdict colouring's own key: how many passages are shaded at each grade, across
                    // every brand. The brand chips mean nothing while passages are shaded by grade, so
                    // this takes their place; the brand names in the text stay marked in their colours.
                    <div className="order-last flex w-full flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-slate-100 pt-2 text-xs">
                      {VERDICT_GRADES_BEST_FIRST.map((grade) => (
                        <span
                          key={grade}
                          className={`inline-flex items-center gap-1.5 text-slate-600 ${verdictLens.counts[grade] ? '' : 'opacity-60'}`}
                          data-verdict-grade={grade}
                        >
                          <span
                            aria-hidden
                            className="inline-block h-2.5 w-2.5 rounded-[3px]"
                            // The grade's colour, from the shared verdict palette.
                            style={{ backgroundColor: VERDICT_LENS_COLORS[grade].solid }}
                          />
                          {PASSAGE_VERDICT_LABELS[grade]}
                          <span className="rounded-full bg-slate-100 px-1.5 text-[0.6875rem] font-semibold leading-4 tabular-nums text-slate-600">
                            {verdictLens.counts[grade]}
                          </span>
                        </span>
                      ))}
                      {verdictLens.passagesGradedDifferently > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-slate-600">
                          {/* A line, as the underline it stands for. */}
                          <span aria-hidden className="inline-block h-0 w-4 border-0 border-b border-dotted border-slate-400" />
                          Brands graded differently
                        </span>
                      ) : null}
                      {VERDICT_GRADES_BEST_FIRST.every((grade) => verdictLens.counts[grade] === 0) ? (
                        <span className="text-slate-500">No passage of this answer is graded.</span>
                      ) : null}
                    </div>
                  ) : highlightBrands && legend.length ? (
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1 gap-y-1 text-xs">
                      {legend.map((item) => (
                        <LegendChip
                          key={item.label}
                          api={api}
                          item={item}
                          domain={entityDomains.get(item.termKeys[0] ?? '')?.domain}
                          hidden={isMuted(item.label)}
                          onToggle={() => toggleBrand(item.label)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex-1" />
                  )}
                  <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
                    {highlightBrands && verdictColouringAvailable ? (
                      <div className="flex items-center gap-2 text-slate-500">
                        <span>Colour passages by</span>
                        <GovernanceSegmentedSwitch
                          value={colouredByVerdict ? 'verdict' : 'brand'}
                          options={PASSAGE_COLOURING_OPTIONS}
                          onChange={setColouring}
                        />
                      </div>
                    ) : null}
                    <label className="mb-0 flex cursor-pointer items-center gap-1.5 font-medium text-slate-600">
                      <input type="checkbox" checked={highlightBrands} onChange={(event) => setHighlightBrands(event.target.checked)} />
                      Highlight brands
                    </label>
                  </div>
                </div>
                {/* The answer and, right after it in reading order, the passage card: beside the answer
                  the card is laid over the sidebar's column, where the columns stack it follows the
                  answer. A plain box, so the answer column's spacing is what it is without the card. */}
                <div>
                  {markdown ? (
                    highlightBrands ? (
                      <HighlightedAnswer
                        markdown={markdown}
                        paintByLine={verdictLens?.lines ?? paintByLine}
                        matchers={matchers}
                        nameByTermKey={nameByTermKey}
                        ownTermKeys={ownTermKeys}
                        cellPaintByLine={verdictLens?.cells ?? cellPaintByLine}
                        spotlightLines={spotlightLines}
                        spotlightCells={spotlightCells}
                        interaction={passageInteraction}
                      />
                    ) : (
                      <MarkdownContentBox
                        markdown={markdown}
                        containerClassName={`${BP_ANSWER_SURFACE_CLASSES} overflow-x-hidden`}
                        contentClassName={BP_ANSWER_PROSE_CLASSES}
                      />
                    )
                  ) : (
                    <div className={`${BP_ANSWER_SURFACE_CLASSES} text-sm text-slate-500`}>
                      The answer text is not available for this run.
                    </div>
                  )}
                  {highlightBrands && passageInteraction ? (
                    <PassageCardLayer
                      gridRef={answerGridRef}
                      sidebarRef={sidebarRef}
                      previewId={hoveredPassageId}
                      pinnedId={pinnedPassageId}
                      renderCard={renderPassageCard}
                    />
                  ) : null}
                </div>
              </div>

              {/* At a glance */}
              <div ref={sidebarRef} className={`space-y-4 ${SIDEBAR_UNDER_CARD_CLASSES}`}>
                {/* Says what the purpose behind this answer measures, so a sidebar that has lost a
                  card does not read as a fault. Silent when nothing is suppressed. */}
                <PurposeKpiScopeNote term={purposeTerm} />

                {/* Recommendation card */}
                {showsRecommendation ? (
                  <div
                    className={`rounded-2xl p-4 shadow-card ring-1 ${
                      !winner
                        ? 'bg-white ring-slate-200/70'
                        : winner.ownership === 'own'
                          ? RECOMMENDATION_CARD_OWN_CLASSES
                          : RECOMMENDATION_CARD_RIVAL_CLASSES
                    }`}
                  >
                    {/* The recommendation reports what the AI actually recommended — a measurement about
                    this answer, not a claim about the customer's taxonomy. It is never muted and never
                    hidden by term status, which is why the name comes straight from the payload rather
                    than through a term resolver. */}
                    <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500 mb-1">
                      AI recommends
                    </div>
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                      <span className="text-base font-bold text-slate-900">
                        {!winner ? 'No clear winner' : winner.ownership === 'own' ? 'You' : winner.name}
                      </span>
                      {/* Gray, never colored: this qualifies the verdict, it is not a fourth verdict. */}
                      {isAmbiguousRecommendation(parsed) ? (
                        <span
                          className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-500"
                          title={AMBIGUOUS_RECOMMENDATION_TOOLTIP}
                        >
                          {AMBIGUOUS_RECOMMENDATION_LABEL}
                        </span>
                      ) : null}
                    </div>
                    {evidenceQuote ? <div className="text-sm text-slate-600 italic">“{evidenceQuote}”</div> : null}
                    {isAmbiguousRecommendation(parsed) ? (
                      <div className="text-xs text-slate-400 mt-2">The AI’s verdict varied between analysis passes.</div>
                    ) : null}
                  </div>
                ) : null}

                {/* An integer placing in THIS answer: no hover and no rank arithmetic, because the
                  mean and its ordinal live on the visibility table instead. Absent for a purpose
                  that names the brand, where the placing is 1 by construction. */}
                {showsPosition ? <AnswerTargetPosition position={targetPosition} /> : null}

                {/* Who's mentioned */}
                <div className={`${BP_CARD_CLASSES} p-4`}>
                  <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500 mb-2">
                    Who’s mentioned
                  </div>
                  {!rankedEntities.length ? (
                    <div className="text-sm text-slate-500">No tracked brands were mentioned in this answer.</div>
                  ) : (
                    <div className="space-y-2">
                      {rankedEntities.map((entity, index) => (
                        <MentionRow
                          key={`${entity.name}-${index}`}
                          api={api}
                          entity={entity}
                          color={colorForEntityName(entityColors, entity.name)}
                          nameColor={readableEntityTextColor(entityColors, colorForEntityName(entityColors, entity.name), {
                            name: entity.name,
                          })}
                          domain={entityDomains.get(mentionTermKey(entity) ?? '')?.domain}
                          untrackedRivals={untrackedCoSubjects}
                          showShareOfVoice={showsShareOfVoice}
                          showSentiment={showsSentiment}
                          onSpotlight={() => setHoveredSubject({ kind: 'entity', name: entity.name })}
                          onClearSpotlight={() => setHoveredSubject(undefined)}
                          onToggleSelected={() => toggleSelectedSubject({ kind: 'entity', name: entity.name })}
                          selected={isSpotlightSubjectSelected(selectedSubjects, { kind: 'entity', name: entity.name })}
                        />
                      ))}
                    </div>
                  )}

                  {/* The untracked tail. Deliberately separated and unmeasured: these carry no share
                    of voice or sentiment, because nothing resolved them to a term to measure
                    against. Showing them at all is the point — the box used to imply the answer
                    discussed only the brands the customer tracks. */}
                  {untrackedCoSubjects.length ? (
                    <div className={rankedEntities.length ? 'mt-3 pt-3 border-t border-slate-100' : ''}>
                      <div
                        className="mb-1 font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500"
                        title="Named in this answer but not tracked in your taxonomy. Add one to start measuring it."
                      >
                        Also named, not tracked
                      </div>
                      <div className="space-y-1">
                        {untrackedCoSubjects.map((entry) => (
                          <div key={entry.norm || entry.name} className="d-flex align-items-center gap-2 text-sm text-slate-600">
                            <span className="truncate">{entry.name}</span>
                            <SuggestedTermIcon />
                            <span className="text-xs text-slate-400 whitespace-nowrap">
                              {entry.unitCount} {entry.unitCount === 1 ? 'mention' : 'mentions'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Attributes in this answer — its own box whenever this purpose measures
                  attributes, so attribute rows can never be mistaken for (or drift into) the
                  mentions box above. Rows come solely from run.aspectScores; when that is absent
                  the box states so rather than rendering zero-filled rows. A purpose that does not
                  measure attributes has no box at all — there was nothing to measure. */}
                {showsAttributes ? (
                  <div className={`${BP_CARD_CLASSES} p-4`}>
                    <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500 mb-2">
                      Attributes in this answer
                    </div>
                    {/* Rows AND columns. Rows alone produced a table with a lone "Attribute" heading and
                      nothing under it — every entity column had been filtered out — which reads as a
                      broken table rather than as an absence of data. A table missing either axis is
                      not a table with less in it; it is not a table. */}
                    {!attributeGroups.length || !attributeEntityKeys.length ? (
                      <div className="text-sm text-slate-500">No attributes were measured for this answer.</div>
                    ) : (
                      // A comparison table: attributes down the side, entities across the top, so one
                      // attribute can be read across every entity at a glance. Plain text throughout —
                      // a badge per cell buried the numbers it was wrapping.
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-slate-400 border-b border-slate-200">
                              <th className="py-1 pe-3 font-semibold">Attribute</th>
                              {attributeEntityKeys.map((entityKey) => {
                                const headerColor = readableEntityTextColor(entityColors, entityColors.byTermKey.get(entityKey), {
                                  termKey: entityKey,
                                });
                                return (
                                  <th key={entityKey} className="py-1 px-2 font-semibold text-center whitespace-nowrap">
                                    <div style={{ color: headerColor }}>
                                      <span className="d-inline-flex align-items-center gap-1">
                                        <EntityFavicon api={api} domain={entityDomains.get(entityKey)?.domain} />
                                        <TaxonomyTermName api={api} termKey={entityKey} context="table-column" />
                                      </span>
                                    </div>
                                    {/* Names what the bars below measure. */}
                                    <div className="text-[10px] font-normal text-slate-400">{ATTRIBUTE_SHARE_COLUMN_LABEL}</div>
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {attributeGroups.map((group) => {
                              // The row carries the attribute's status, so its numbers mute with its
                              // name — confident figures beside a hedged label are worse than either.
                              const rowPresentation = termStatusPresentation(attributeStatusOf(group.aspectTermKey), 'table-row');
                              if (rowPresentation.hidden) {
                                return null;
                              }
                              return (
                                <tr
                                  key={group.aspectTermKey}
                                  className={[
                                    SPOTLIGHT_TABLE_ROW_CLASS_NAME,
                                    spotlightRowStateClassName(
                                      isSpotlightSubjectSelected(selectedSubjects, { kind: 'aspect', termKey: group.aspectTermKey })
                                    ),
                                    rowPresentation.className,
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                  title={rowPresentation.title}
                                  aria-pressed={isSpotlightSubjectSelected(selectedSubjects, {
                                    kind: 'aspect',
                                    termKey: group.aspectTermKey,
                                  })}
                                  onMouseEnter={() => setHoveredSubject({ kind: 'aspect', termKey: group.aspectTermKey })}
                                  onMouseLeave={() => setHoveredSubject(undefined)}
                                  onClick={() => toggleSelectedSubject({ kind: 'aspect', termKey: group.aspectTermKey })}
                                >
                                  <td className="py-1 pe-3 text-slate-700">
                                    <TaxonomyTermName api={api} termKey={group.aspectTermKey} className="font-semibold" neverMute />
                                    {rowPresentation.showSuggestionIcon ? <SuggestedTermIcon /> : null}
                                  </td>
                                  {attributeEntityKeys.map((entityKey) => {
                                    const entry = group.entries.find((candidate) => candidate.entityKey === entityKey);
                                    return (
                                      <td key={entityKey} className="py-1 px-2 text-center whitespace-nowrap">
                                        {entry ? (
                                          (() => {
                                            const barDescription = describeAttributeBar(
                                              shareToPercent(entry.share),
                                              attributeCeiling === undefined ? undefined : shareToPercent(attributeCeiling),
                                              nameByTermKey.get(entityKey),
                                              attributeNameOf(group.aspectTermKey)
                                            );
                                            const verdict = attributeVerdictFor(attributeVerdicts, {
                                              attributeTermKey: group.aspectTermKey,
                                              brandTermKey: entityKey,
                                            });
                                            return (
                                              <span className="d-inline-block" style={{ minWidth: '3.5rem' }}>
                                                {/* The verdict of the drawn passages a click on this row lights for this brand, on
                                                  the verdict's bands and under its passages' own label — so it reads as they
                                                  do. It waits for the analysed answer while that loads, and shows nothing
                                                  where the answer could not be loaded (the answer area says its text is not
                                                  available): "not measured" would claim the passages carry no grade. The
                                                  verdict is a measurement of tone, so a purpose that does not take tone
                                                  shows none, as the passage cards show none. */}
                                                {showsSentiment && !parsedFailed ? (
                                                  <span className="d-inline-flex align-items-center gap-1 justify-content-center">
                                                    {parsedLoading ? (
                                                      <SkeletonBlock className="h-2.5 w-3" />
                                                    ) : (
                                                      <SentimentThumb
                                                        tone={verdict?.tone}
                                                        metric="verdict"
                                                        className="text-[10px]"
                                                        title={ATTRIBUTE_VERDICT_HINT}
                                                        description={verdict ? PASSAGE_VERDICT_LABELS[verdict.grade] : undefined}
                                                      />
                                                    )}
                                                  </span>
                                                ) : null}
                                                {/* No printed percentage: the bar's length is the reading, drawn against the
                                                  most-discussed attribute in this answer so a share of a hundredth still
                                                  shows. The exact figure is the bar's name and tooltip. */}
                                                <span
                                                  role="img"
                                                  aria-label={barDescription}
                                                  title={barDescription}
                                                  className="d-block mt-0.5 h-1 rounded bg-slate-100 overflow-hidden"
                                                >
                                                  <span
                                                    className="d-block h-full"
                                                    style={{
                                                      width: `${attributeBarWidthPercent(entry.share, attributeCeiling)}%`,
                                                      backgroundColor: entityColors.byTermKey.get(entityKey) || '#94a3b8',
                                                      opacity: 0.55,
                                                    }}
                                                  />
                                                </span>
                                              </span>
                                            );
                                          })()
                                        ) : (
                                          <span
                                            className="text-slate-300"
                                            title={`Not measured for ${nameByTermKey.get(entityKey) ?? 'this entity'}`}
                                          >
                                            —
                                          </span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Sources cited — grouped by website, from the run's own citation mirror. */}
                <AnswerCitedSources
                  api={api}
                  citationHosts={run.citationHosts}
                  citations={parsed?.citations as readonly CitationLike[] | undefined}
                  parsedLoading={parsedLoading}
                  parsedFailed={parsedFailed}
                  grouping={sourceGrouping}
                  onGroupingChange={setSourceGrouping}
                  authoritySources={authoritySources}
                  sourcesReady={sourcesReady}
                  colorByTermKey={entityColors.byTermKey}
                  storedTermKeys={entityColors.storedTermKeys}
                  labelByTermKey={nameByTermKey}
                  ownTermKeys={ownTermKeys}
                />
              </div>
            </div>
          )}
        </main>
      </div>
    </FaviconLoadGate>
  );
}
